import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { VaFileInput } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import PropTypes from 'prop-types';
import vaFileInputFieldMapping from './vaFileInputFieldMapping';
import {
  useFileUpload,
  getFileError,
  simulateUploadSingle,
  getMockFileData,
  VaProgressUploadAnnounce,
} from './vaFileInputFieldHelpers';
import navigationState from '../utilities/navigation/navigationState';
import passwordErrorState from '../utilities/file/passwordErrorState';
import {
  BACKEND_INCORRECT_PASSWORD_MSG,
  INCORRECT_PASSWORD_ERROR,
} from '../validation';

/**
 * Usage uiSchema:
 * ```
 * fileInput: {
 *   'ui:title': 'A file input',
 *   'ui:webComponentField': VaFileInput,
 *   'ui:hint': 'hint',
 *   'ui:errorMessages': {
 *     required: 'This is a custom error message.',
 *   },
 *   formNumber: '20-10206'
 *   fileUploadUrl: 'https://api.test.gov',
 *   'ui:options': {
 *     accept: '.pdf,.jpeg,.png',
 *     enableAnalytics: true,
 *     encrypted: true,
 *     maxFileSize: 1048576
 *   },
 * }
 * ```
 *
 * Usage schema:
 * ```
 * uploadedFile: {
 *   type: 'object',
 *    properties: {
 *       confirmationCode: {
 *         type: 'string',
 *       },
 *       isEncrypted: {
 *         type: 'boolean',
 *       },
 *       password: {
 *         type: 'string',
 *       },
 *       name: {
 *         type: 'string',
 *       },
 *       size: {
 *         type: 'integer',
 *       },
 *       fileType: {
 *         type: 'string',
 *       },
 *       warnings: {
 *         type: 'array',
 *         items: {
 *           type: 'string',
 *         },
 *       },
 *       additionalData: {
 *         type: 'object',
 *         properties: {},
 *       },
 *     },
 * },
 * ```


 * @param {WebComponentFieldProps} props */
const VaFileInputField = props => {
  const { uiOptions = {}, childrenProps } = props;
  const mappedProps = vaFileInputFieldMapping(props);
  const dispatch = useDispatch();
  const [error, setError] = useState(mappedProps.error);
  const [fileWithPassword, setFileWithPassword] = useState(null);
  const { percentUploaded, handleUpload } = useFileUpload(
    uiOptions,
    mappedProps.accept,
    dispatch,
  );
  const [encrypted, setEncrypted] = useState(false);
  const [passwordErrorManager, setPasswordErrorManager] = useState(null);
  const [percent, setPercent] = useState(null);
  const [passwordSubmissionSuccess, setPasswordSubmissionSuccess] =
    useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const requestController = useRef(null);

  const idNo = childrenProps.idSchema.$id;
  const showIfNavigated = value =>
    navigationState.getNavigationAttemptStatus() ? value : null;

  // only needed because sometimes we skip upload and simulate percent progress
  useEffect(() => {
    setPercent(percentUploaded);
  }, [percentUploaded]);

  useEffect(() => {
    const instance = passwordErrorState.getInstance(idNo);
    setPasswordErrorManager(instance);
    return () => {
      instance.reset();
    };
  }, []);

  const getErrorMessage = field => {
    let errorMessage = null;
    const errorArray = childrenProps.errorSchema[field]?.__errors;
    if (errorArray && errorArray.length > 0) {
      errorMessage = errorArray[0];
    }
    return errorMessage;
  };

  const additionalInputError = getErrorMessage('additionalData');
  const missingPasswordError = getErrorMessage('isEncrypted');

  const assignFileUploadToStore = uploadedFile => {
    if (!uploadedFile) return;

    const { file, ...rest } = uploadedFile;

    const { name, size, type } = file;

    // don't merge previous form data, it keeps errorMessage from a failed attempt
    childrenProps.onChange({
      ...rest,
      name,
      size,
      type,
      idNo,
      // reset additionalData when adding a file
      additionalData: {},
    });
  };

  // set errors and related statuses based on upload result
  const handleSubmissionErrors = ({ errorMessage, isEncrypted }) => {
    const hasWrongPassword =
      isEncrypted && errorMessage === BACKEND_INCORRECT_PASSWORD_MSG;
    passwordErrorManager.setHasWrongPassword(hasWrongPassword);
    // for wrong password error message will be set on text input inside shadow dom not parent
    const _errorMessage = hasWrongPassword ? ' ' : errorMessage;
    setError(_errorMessage);
    // file was encrypted
    if (isEncrypted) {
      setPasswordSubmissionSuccess(!errorMessage);
      const _passwordError = hasWrongPassword ? INCORRECT_PASSWORD_ERROR : null;
      setPasswordError(_passwordError);
      // file was successfully decrypted
      if (!errorMessage) {
        passwordErrorManager.setHasPassword(true);
        setPasswordError(null);
      }
    }
  };

  const handleFileProcessing = uploadedFile => {
    if (
      !uploadedFile ||
      !uploadedFile.file ||
      (requestController.current && requestController.current?.signal?.aborted)
    )
      return;
    requestController.current = null;
    handleSubmissionErrors(uploadedFile);

    if (!uploadedFile.errorMessage) {
      uiOptions.onUploadSuccess?.({ uploadedFile });
    }
    assignFileUploadToStore(uploadedFile);
  };

  // a new file never inherits the previous file's password attempt
  const setPasswordStateForFile = needsPassword => {
    passwordErrorManager.setNeedsPassword(needsPassword);
    passwordErrorManager.setHasPassword(false);
    passwordErrorManager.setHasWrongPassword(false);
    setPasswordError(null);
    setPasswordSubmissionSuccess(null);
    setEncrypted(needsPassword);
    if (!needsPassword) {
      setFileWithPassword(null);
    }
  };

  const handleVaChange = async e => {
    // clear error
    setError(null);
    // reset in case user is replacing one encrypted file with another
    setPasswordSubmissionSuccess(null);
    const fileFromEvent = e.detail.files[0];

    if (!fileFromEvent) {
      // if there is a request in flight, cancel it
      requestController.current?.abort?.();
      setPercent(null);
      setError(mappedProps.error);
      setPasswordStateForFile(false);
      childrenProps.onChange({});
      return;
    }

    // create a new controller for each upload
    requestController.current = new AbortController();

    const { fileError, encryptedCheck } = await getFileError(
      fileFromEvent,
      uiOptions,
    );

    if (fileError) {
      setError(fileError);
      setPasswordStateForFile(false);
      childrenProps.onChange({});
      return;
    }

    // file ok
    setError(null);
    setPasswordStateForFile(encryptedCheck);

    if (uiOptions.skipUpload && !encryptedCheck) {
      simulateUploadSingle(
        setPercent,
        childrenProps.onChange,
        fileFromEvent,
        requestController.current,
      );
      return;
    }

    // delay uploading for encrypted files until password is entered
    if (encryptedCheck) {
      setFileWithPassword(fileFromEvent);
      childrenProps.onChange({
        isEncrypted: encryptedCheck,
        idNo,
      });
    } else {
      handleUpload(
        fileFromEvent,
        handleFileProcessing,
        null,
        null,
        requestController.current,
      );
    }
  };

  const handleVaPasswordSubmit = e => {
    if (!fileWithPassword) {
      setError("We couldn't upload your file. Try again.");
      return;
    }
    const { password } = e.detail;
    // null is the component's pending state, so a stale failure is not
    // re-applied while this request is in flight
    setPasswordSubmissionSuccess(null);
    setPasswordError(null);
    setError(null);

    if (uiOptions.skipUpload) {
      // simulate upload
      setTimeout(() => {
        childrenProps.onChange({
          ...getMockFileData(fileWithPassword),
        });
        handleSubmissionErrors({ errorMessage: null, isEncrypted: true });
      }, 500);
    } else {
      handleUpload(
        fileWithPassword,
        handleFileProcessing,
        password,
        null,
        requestController.current,
      );
    }
  };

  const handleAdditionalInput = e => {
    if (mappedProps.handleAdditionalInput) {
      const payload = mappedProps.handleAdditionalInput(e);
      childrenProps.onChange({
        idNo,
        ...childrenProps.formData,
        additionalData: payload,
      });
    }
  };

  const handleInternalError = e => {
    const { error: _error } = e.detail;
    if (_error) {
      setError(_error);
      // the component rejected the file without emitting vaChange
      setPasswordStateForFile(false);
      childrenProps.onChange({});
    }
  };

  const _error = error || showIfNavigated(mappedProps.error);
  const _passwordError = passwordError || showIfNavigated(missingPasswordError);
  const fileHasBeenAdded =
    childrenProps.formData.name && childrenProps.formData.name !== 'uploading';

  return (
    <>
      <VaProgressUploadAnnounce uploading={!!percent} />
      <VaFileInput
        data-dd-privacy="mask"
        {...mappedProps}
        error={_error}
        encrypted={encrypted}
        uploadedFile={mappedProps.uploadedFile}
        onVaFileInputError={handleInternalError}
        onVaChange={handleVaChange}
        onVaPasswordSubmit={handleVaPasswordSubmit}
        passwordSubmissionSuccess={passwordSubmissionSuccess}
        percentUploaded={encrypted ? null : percent}
        passwordError={_passwordError}
      >
        <div className="additional-input-container">
          {fileHasBeenAdded &&
            mappedProps.additionalInput &&
            React.cloneElement(
              // clone element so we can attach listeners
              mappedProps.additionalInput(
                additionalInputError,
                childrenProps.formData.additionalData || {},
                {
                  labels: uiOptions.additionalInputLabels,
                  title: uiOptions.additionalInputTitle,
                },
              ),
              {
                // attach other listeners as needed
                onVaChange: handleAdditionalInput,
                onVaSelect: handleAdditionalInput,
                onVaValueChange: handleAdditionalInput,
              },
            )}
        </div>
      </VaFileInput>
    </>
  );
};

VaFileInputField.propTypes = {
  childrenProps: PropTypes.object.isRequired,
  uiOptions: PropTypes.shape({
    maxFileSize: PropTypes.number,
    formNumber: PropTypes.string,
    onUploadSuccess: PropTypes.func,
  }),
  onVaChange: PropTypes.func,
};

VaFileInputField.defaultProps = {
  uiOptions: {},
};

export default VaFileInputField;
